import apiClient from "./apiClient";

const requestCache = {};

const dedupGet = (key, fetchFn, ttl = 300000) => {
  const now = Date.now();
  if (requestCache[key] && (now - requestCache[key].timestamp < ttl)) {
    return requestCache[key].promise;
  }
  const promise = fetchFn();
  requestCache[key] = { promise, timestamp: now };
  promise.catch(() => { delete requestCache[key]; });
  return promise;
};

export const clearProjectCache = () => {
  for (const k in requestCache) {
    delete requestCache[k];
  }
};

/* =====================================================
   PROJECTS API — cached
===================================================== */

export const getProjects = () =>
  dedupGet("projects", () => apiClient.get("/projects/api/projects/"));

export const createProject = (data) => {
  clearProjectCache();
  return apiClient.post("/projects/api/projects/", data);
};

export const getProjectDetails = (projectId) =>
  dedupGet(`project_${projectId}`, () => apiClient.get(`/projects/api/projects/${projectId}/`));

export const updateProject = (projectId, data) => {
  clearProjectCache();
  return apiClient.patch(`/projects/api/projects/${projectId}/`, data);
};

export const deleteProject = (projectId) => {
  clearProjectCache();
  return apiClient.delete(`/projects/api/projects/${projectId}/`);
};

export const getProjectProgress = (projectId) =>
  dedupGet(`progress_${projectId}`, () => apiClient.get(`/projects/api/projects/${projectId}/progress/`));


/* =====================================================
   PROJECT LOCATIONS  ← NEW
===================================================== */

export const getProjectLocations = () =>
  dedupGet("locations", () => apiClient.get("/projects/api/locations/"));

export const createProjectLocation = (data) => {
  clearProjectCache();
  return apiClient.post("/projects/api/locations/", data);
};

export const updateProjectLocation = (id, data) => {
  clearProjectCache();
  return apiClient.patch(`/projects/api/locations/${id}/`, data);
};


/* =====================================================
   PROJECT DISCIPLINES API — cached
===================================================== */

export const getProjectDisciplines = (projectId) =>
  dedupGet(`disciplines_${projectId}`, () => apiClient.get(`/projects/api/projects/${projectId}/disciplines/`));

export const createProjectDiscipline = (projectId, data) => {
  clearProjectCache();
  return apiClient.post(`/projects/api/projects/${projectId}/disciplines/`, data);
};

export const getProjectDiscipline = (projectId, disciplineId) =>
  dedupGet(`discipline_${projectId}_${disciplineId}`, () => apiClient.get(`/projects/api/projects/${projectId}/disciplines/${disciplineId}/`));

export const updateProjectDiscipline = (projectId, disciplineId, data) => {
  clearProjectCache();
  return apiClient.patch(`/projects/api/projects/${projectId}/disciplines/${disciplineId}/`, data);
};

export const deleteProjectDiscipline = (projectId, disciplineId) => {
  clearProjectCache();
  return apiClient.delete(`/projects/api/projects/${projectId}/disciplines/${disciplineId}/`);
};


/* =====================================================
   DISCIPLINE ASSIGNMENT API — cached
===================================================== */

export const assignDisciplineEmployees = (projectId, disciplineId, data) => {
  clearProjectCache();
  return apiClient.post(`/projects/api/projects/${projectId}/disciplines/${disciplineId}/assign/`, data);
};


/* =====================================================
   MANAGER ACTIVITIES API — cached
===================================================== */

export const getManagerActivities = (params = {}) =>
  dedupGet(`manager_activities_${JSON.stringify(params)}`, () => apiClient.get("/projects/api/manager-activities/", { params }));

export const createManagerActivity = (data) => {
  clearProjectCache();
  return apiClient.post("/projects/api/manager-activities/", data);
};

export const getManagerActivity = (activityId) =>
  dedupGet(`manager_activity_${activityId}`, () => apiClient.get(`/projects/api/manager-activities/${activityId}/`));

export const updateManagerActivity = (activityId, data, params = {}) => {
  clearProjectCache();
  return apiClient.patch(`/projects/api/manager-activities/${activityId}/`, data, { params });
};

export const deleteManagerActivity = (activityId, params = {}) => {
  clearProjectCache();
  return apiClient.delete(`/projects/api/manager-activities/${activityId}/`, { params });
};

export const getManagerProgress = (params = {}) =>
  dedupGet(`manager_progress_${JSON.stringify(params)}`, () => apiClient.get("/projects/api/manager-activities/progress/", { params }));


/* =====================================================
   STAGES API
===================================================== */

export const createStage = (projectId, disciplineId, data) => {
  clearProjectCache();
  return apiClient.post(`/projects/api/projects/${projectId}/disciplines/${disciplineId}/stages/`, data);
};

export const getDisciplineStages = (projectId, disciplineId) =>
  dedupGet(`stages_${projectId}_${disciplineId}`, () => apiClient.get(`/projects/api/projects/${projectId}/disciplines/${disciplineId}/stages/`));

export const bulkCreateStages = (projectId, disciplineId, stages) => {
  clearProjectCache();
  return apiClient.post(`/projects/api/projects/${projectId}/disciplines/${disciplineId}/stages/bulk/`, { stages });
};
